import { useEffect, useState } from "react";
import { Search, LogOut, LayoutDashboard, Briefcase, Clock, Receipt, MoreHorizontal, ShieldCheck, Lock, CalendarDays, FileText } from "lucide-react";
import { Link, Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import BackButton from "@/components/BackButton";
import { usePayslipsVisible } from "@/hooks/useLeaveVisible";

const Item = ({ to, label, testid }) => (
  <NavLink to={to} data-testid={testid}
    className={({ isActive }) =>
      `px-3 py-2 rounded ${isActive ? "bg-silver text-black" : "text-white hover:text-silver"}`
    }>{label}</NavLink>
);

const Tab = ({ to, label, Icon, testid, end = false }) => (
  <NavLink to={to} end={end} data-testid={testid}
    className={({ isActive }) =>
      `flex-1 flex flex-col items-center justify-center py-1.5 text-[11px] ${isActive ? "text-silver" : "text-white/70"}`
    }>
    <Icon size={18}/><span className="mt-0.5">{label}</span>
  </NavLink>
);

export default function MainLayout() {
  const { user, logout, logoutAllDevices } = useAuth();
  const nav = useNavigate();
  const isWorker = user?.role === "Worker";
  const payslipsOn = usePayslipsVisible();
  const [q, setQ] = useState(""); const [res, setRes] = useState([]); const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q || q.length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q } }); setRes(data.results||[]); setOpen(true); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const workerTimesheets = "/my/timesheets";

  return (
    <div className="min-h-screen bg-black text-white pb-16 md:pb-0">
      {/* Desktop top bar (hidden for Worker — Worker uses only the mobile tab bar everywhere) */}
      <header className={`${isWorker ? "hidden" : "hidden md:flex"} border-b border-neutral-800 px-4 py-3 flex-wrap items-center gap-4`} data-testid="main-nav">
        <Link to="/" className="font-bold tracking-widest text-silver border border-silver px-3 py-1">
          LW&amp;Co Hub
        </Link>
        <nav className="flex gap-1 flex-wrap">
          <Item to="/" label="Dashboard" testid="nav-dashboard" />
          <Item to="/projects" label="Projects" testid="nav-projects" />
          <Item to="/operations/bills" label="Bills" testid="nav-bills" />
          <Item to="/operations/timesheets" label="Timesheets" testid="nav-timesheets" />
          <Item to="/operations/payroll" label="Pay run" testid="nav-pay-run" />
          <Item to="/cost-report" label="Cost report" testid="nav-cost-report" />
          <Item to="/hs" label="H&S" testid="nav-hs" />
          {user?.role === "Admin" && <Item to="/configuration" label="Configuration" testid="nav-config" />}
        </nav>
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-silver"/>
          <input data-testid="global-search" className="input pl-7 w-56" placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)} onFocus={()=>setOpen(res.length>0)} onBlur={()=>setTimeout(()=>setOpen(false),200)}/>
          {open && res.length > 0 && (
            <ul className="absolute right-0 top-10 w-72 bg-neutral-900 border border-neutral-700 rounded max-h-80 overflow-y-auto z-20">
              {res.map((r, i) => (
                <li key={i} className="px-3 py-2 text-sm hover:bg-neutral-800 cursor-pointer"
                  onMouseDown={() => { nav(r.url); setOpen(false); setQ(""); }}>
                  <span className="opacity-50">{r.type}</span> · {r.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="btn-change-password" onClick={() => nav("/change-password")} className="btn-ghost text-sm">
            Change password
          </button>
          <button data-testid="btn-logout-all" onClick={async()=>{ if(!window.confirm("Log out of all devices now? You'll need to sign in again.")) return; await logoutAllDevices(); }} className="btn-ghost text-sm">
            Log out all devices
          </button>
          <span className="text-sm text-silver" data-testid="user-chip">
            {user?.name} <span className="opacity-60">({user?.role})</span>
          </span>
          <button data-testid="btn-logout" onClick={logout} className="btn-ghost text-sm flex items-center gap-2">
            <LogOut size={14}/> Logout
          </button>
        </div>
      </header>

      {/* Mobile top bar (brand + user + logout) — always shown on narrow screens; also shown on desktop for Worker */}
      <header className={`${isWorker ? "flex md:flex" : "md:hidden flex"} border-b border-neutral-800 px-3 py-2 items-center gap-2`} data-testid="mobile-top-nav">
        <Link to="/" className="font-bold tracking-widest text-silver border border-silver px-2 py-0.5 text-xs">LW&amp;Co Hub</Link>
        {isWorker ? (
          <div className="flex-1 text-xs text-silver truncate" data-testid="worker-user-chip">{user?.name}</div>
        ) : (
        <div className="relative flex-1">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-silver"/>
          <input data-testid="global-search-mobile" className="input pl-6 w-full text-sm" placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)}/>
          {open && res.length > 0 && (
            <ul className="absolute left-0 right-0 top-9 bg-neutral-900 border border-neutral-700 rounded max-h-80 overflow-y-auto z-20">
              {res.map((r, i) => (
                <li key={i} className="px-3 py-2 text-sm hover:bg-neutral-800 cursor-pointer"
                  onMouseDown={() => { nav(r.url); setOpen(false); setQ(""); }}>
                  <span className="opacity-50">{r.type}</span> · {r.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
        {isWorker && (
          <button data-testid="btn-change-password-worker" onClick={() => nav("/change-password")} className="btn-ghost text-xs flex items-center gap-1 px-2 py-1" title="Change password">
            <Lock size={12}/>
          </button>
        )}
        <button data-testid="btn-logout-mobile" onClick={logout} className="btn-ghost text-xs flex items-center gap-1 px-2 py-1">
          <LogOut size={12}/>
        </button>
      </header>

      <main className="p-4 md:p-6">
        <BackButton />
        <Outlet />
      </main>

      {/* Mobile bottom tab bar — shown on mobile for everyone; also shown on desktop for Worker so they always have a nav */}
      <nav className={`${isWorker ? "flex md:flex" : "md:hidden flex"} fixed bottom-0 inset-x-0 z-30 bg-black border-t border-neutral-800`} data-testid="mobile-tabbar">
        {isWorker ? (
          <>
            <Tab to="/my/projects" label="Projects" Icon={Briefcase} testid="tab-worker-projects"/>
            <Tab to="/my/timesheets" label="Timesheets" Icon={Clock} testid="tab-timesheets"/>
            <Tab to="/my/leave" label="Leave" Icon={CalendarDays} testid="tab-worker-leave"/>
            <Tab to="/my/hs" label="H&S" Icon={ShieldCheck} testid="tab-worker-hs"/>
            {payslipsOn && <Tab to="/my/payslips" label="Payslips" Icon={FileText} testid="tab-worker-payslips"/>}
          </>
        ) : (
          <>
            <Tab to="/" end label="Dashboard" Icon={LayoutDashboard} testid="tab-dashboard"/>
            <Tab to="/projects" label="Projects" Icon={Briefcase} testid="tab-projects"/>
            <Tab to="/my/timesheets" label="Timesheets" Icon={Clock} testid="tab-timesheets"/>
            <Tab to="/operations/bills" label="Bills" Icon={Receipt} testid="tab-bills"/>
            <Tab to="/more" label="More" Icon={MoreHorizontal} testid="tab-more"/>
          </>
        )}
      </nav>
    </div>
  );
}
